'use client';

import { useMemo } from 'react';

import type { CalculatorDto } from '@/lib/api/tools';

import type { CostCalculatorBreed } from '../data/costCalculatorBreeds';
import useCostCalculatorState from './useCostCalculatorState';
import { useCostCalculatorClient } from './useCostCalculatorClient';
import { useSharedCalculationId } from './useSharedCalculationId';

export type UseCostCalculatorExperienceArgs = {
  calculator: CalculatorDto;
  initialBreed?: string;
  breeds?: CostCalculatorBreed[];
};

export function useCostCalculatorExperience({
  calculator,
  initialBreed,
  breeds
}: UseCostCalculatorExperienceArgs) {
  const calculationId = useSharedCalculationId();
  const {
    inputs,
    setInputs,
    selections,
    onSelect,
    resultsList,
    sectionTotals,
    defaultOutput,
    hasResults,
    resultsTitle,
    expandedSections,
    setExpandedSections,
    showCalculator,
    setShowCalculator,
    handleCopyShare,
    handleEmailSubmit,
    isSaving,
    handleClear,
    recomputeResults,
    selectedBreed
  } = useCostCalculatorState(calculator, calculationId, initialBreed, breeds);

  const {
    sectionsAnchorRef,
    scrollToResults,
    handleGetStarted,
    handleToggleSection,
    handleExpandNext,
    handleCalculateAndScroll
  } = useCostCalculatorClient({
    calculator,
    recomputeResults,
    setExpandedSections,
    setShowCalculator
  });

  const resolvedBreeds = useMemo(() => breeds ?? [], [breeds]);

  const normalizedCalculatorInputs = useMemo(
    () =>
      (calculator.inputs ?? []).map((input) => ({
        ...input,
        options: input.options ?? undefined
      })),
    [calculator.inputs]
  );

  return {
    inputs,
    setInputs,
    selections,
    onSelect,
    resultsList,
    sectionTotals,
    defaultOutput,
    hasResults,
    resultsTitle,
    expandedSections,
    showCalculator,
    handleCopyShare,
    handleEmailSubmit,
    isSaving,
    handleClear,
    recomputeResults,
    selectedBreed,
    sectionsAnchorRef,
    scrollToResults,
    handleGetStarted,
    handleToggleSection,
    handleExpandNext,
    handleCalculateAndScroll,
    resolvedBreeds,
    normalizedCalculatorInputs
  };
}
